import React, { useState } from 'react';

import { useAccountContext } from '@/lib/accounts/context';
import { Button } from '@/components/button';
import { Input } from '@/components/form/input';
import { useCreateNotionIntegrationMutation } from '@/graphql';
import { showChatWidget } from '@/lib/utils/support';

import formStyles from '@/components/form/form.module.scss';
import styles from './../integrations.module.scss';

interface NotionFormProps {
  onSuccess(): void;
  onClose(): void;
}

export const NotionForm = ({ onSuccess, onClose }: NotionFormProps) => {
  const { account } = useAccountContext();
  const [createNotionIntegration, { loading }] = useCreateNotionIntegrationMutation();

  const [showNotionError, setShowNotionError] = useState(false);
  const [notionApiKey, setNotionApiKey] = useState('');

  const handleAddNotion = async (event: React.FormEvent) => {
    event.preventDefault();
    const { data } = await createNotionIntegration({
      variables: { apiKey: notionApiKey, accountId: account.accountId },
      refetchQueries: ['AccountSettings'],
    });
    const success = data?.createNotionIntegration.success;

    if (success === false) {
      return setShowNotionError(true);
    }

    setNotionApiKey('');
    setShowNotionError(false);
    onSuccess();
  };

  return (
    <>
      <section className={styles.integrationModal}>
        <h2>Integrate Notion</h2>
        <p>
          To integrate Notion, follow the steps{' '}
          <a
            href="https://docs.supersimple.io/integrations/notion"
            target="_blank"
            rel="noopener noreferrer">
            in our Notion guide
          </a>{' '}
          and set up your integration.
          <br />
          Don&apos;t forget to give your integration the necessary permissions on Notion.
        </p>
        <p>Paste your API secret below.</p>
      </section>
      <form className={styles.integrationForm} onSubmit={handleAddNotion}>
        {showNotionError && (
          <>
            <p className={styles.integrationError}>Unable to authenticate with Notion.</p>
            <p>
              If you need further assistance, you can always{' '}
              <a href="#" onClick={showChatWidget}>
                chat with us
              </a>
              .
            </p>
          </>
        )}
        <Input
          value={notionApiKey}
          placeholder="Notion API secret"
          name="token"
          type="password"
          size="regular"
          autoComplete="off"
          data-1p-ignore
          autoFocus
          onChange={(e) => setNotionApiKey(e.currentTarget.value ?? '')}
        />

        <footer className={formStyles.formControls}>
          <Button type="submit" loading={loading} disabled={loading || !notionApiKey.length}>
            Add Notion
          </Button>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        </footer>
      </form>
    </>
  );
};
